import { cn } from '@ui-kit/utils';
import { type ComponentProps, useId } from 'react';

type Props = ComponentProps<'textarea'> & {
  error?: boolean;
  errorMessage?: string;
};

const textareaClassName = cn(
  'border-border-default w-full rounded-sm border px-3 py-2',
  'caret-text-brand transition-all duration-250 ease-out',
  'focus:border-border-brand focus:outline-none',
  'disabled:border-border-disabled disabled:text-text-disabled',
  'aria-invalid:caret-text-error aria-invalid:border-border-error',
  'focus:aria-invalid:border-border-error',
);

const Textarea = ({ errorMessage, error = !!errorMessage, className, ...restProps }: Props) => {
  const errorMessageId = useId();

  return (
    <div className="flex flex-col gap-1">
      <textarea
        className={cn(textareaClassName, className)}
        {...(error ? { 'aria-invalid': true } : {})}
        {...(errorMessage ? { 'aria-errormessage': errorMessageId } : {})}
        {...restProps}
      />
      {/* 
        The element needs to be visible at all time due to aria-live="polite"
        but it should not take up space when there is no error message or
        the textarea is not invalid. 
      */}
      <span
        className={cn('text-text-error text-caption-semibold sr-only mt-1 ml-4', {
          'not-sr-only': error && !!errorMessage,
        })}
        id={errorMessageId}
        aria-live="polite"
      >
        {!!errorMessage && errorMessage}
      </span>
    </div>
  );
};

export default Textarea;
